'use client';
import { Info } from '@phosphor-icons/react';
import React, { useId } from 'react';
import IconWrapper from '../IconWrapper/IconWrapper';
import { LanguageType, LanguageDirection } from '@/lib/types';

type InputFieldState = "normal" | "disabled" | "filled" | "invalid";


interface InputFieldProps {
	label: string;
	size: number;
	hint?: string;
	language?: LanguageType;
	disabled?: boolean;
	invalid?: boolean;
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	placeholder?: string;
	optional?: boolean;
}

const LANGUAGE_TO_FLEX_PLACEMENT: Record<LanguageType, string> = {
	english: "start",
	arabic: "end"
};


const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
};

const LANGUAGE_TO_COUNTER_POSITION: Record<LanguageType, string> = {
	english: "right-3",
	arabic: "left-3"
};

const SIZE_TO_TEXT_STYLE: Record<number, string> = {
	20: 'text-20-to-18--alexandria',
	18: 'text-18-fixed--alexandria',
};

const STATE_TO_BORDER: Record<InputFieldState, string> = {
	normal: `
		border-stroke-soft-200
		focus-within:border-theme-yellow-primary-base
	`,
	filled: `
		border-theme-yellow-primary-base
	`,
	disabled: `
		border-stroke-disabled
	`,
	invalid: `
		border-state-error-base
	`
};

const STATE_TO_LABEL_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-text-tertiary
		group-hover:text-text-quinary
	`,
	filled: `
		text-text-tertiary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-text-tertiary
	`,
};

const STATE_TO_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-text-disabled
	`,
	filled: `
		text-text-primary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-text-primary
	`,
};

const STATE_TO_HINT_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-text-quaternary
	`,
	filled: `
		text-text-quaternary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-state-error-base
	`,
};
const STATE_TO_ICON_TEXT: Record<InputFieldState, string> = {
	normal: `
		text-graphics-quaternary
	`,
	filled: `
		text-graphics-quaternary
	`,
	disabled: `
		text-graphics-disabled
	`,
	invalid: `
		text-state-error-base
	`,
};

const getState = (disabled: boolean, filled: boolean, invalid: boolean): InputFieldState => {
	if (disabled) return "disabled";
	if (invalid) return "invalid";
	if (filled) return "filled";
	return "normal";
};


const TextAreaField: React.FC<InputFieldProps> = ({
	label,
	size,
	hint,
	language = "english",
	disabled = false,
	invalid = false,
	value,
	onChange,
	placeholder,
	optional = false
}) => {
	const inputId = useId();
	const hintId = useId();
	const counterId = useId();

	const isFilled = value?.trim() !== '';

	// const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
	// 	const newText = e.target.value;
	// 	const remainingChars = 500 - value.length;
		
	// 	if (newText.length <= 500) {
	// 		setValue(newText);
	// 		if (isInvalid && newText.length < 500) {
	// 			setIsInvalid(false);
	// 		}
	// 	} else {
	// 		// If we're adding text and there's room for some of it
	// 		if (newText.length > value.length && remainingChars > 0) {
	// 			// Take only the chars that fit within the limit
	// 			const partialText = newText.slice(0, value.length + remainingChars);
	// 			setValue(partialText);
	// 		}
	// 		setIsInvalid(true);
	// 	}
	// };

	return (
		<div className="w-full tb:min-w-[18rem] flex flex-col gap-[0.75rem] group">
			<div
				className={`
					flex flex-col gap-[1rem]
					border-b-main
					transition-[border, colors] duration-500 ease-in-out
					${STATE_TO_BORDER[getState(disabled, isFilled, invalid)]}
					${`items-${LANGUAGE_TO_FLEX_PLACEMENT[language]}`}
				`}
			>
				<div
					style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
					className={`
						flex flex-row gap-[0.125rem]
						items-center
						${`justify-${LANGUAGE_TO_FLEX_PLACEMENT[language]}`}
					`}
				>
				<label
					htmlFor={inputId}
					className={`
						text-16-fixed--alexandria
						transition-colors duration-300 ease-in-out
						${STATE_TO_LABEL_TEXT[getState(disabled, isFilled, invalid)]}
					`}
				>
						{label}
					</label>
					{!optional && <p aria-hidden="true" className="text-theme-yellow-primary-base">*</p>}
				</div>

				<div className="relative w-full">
					<textarea
						suppressHydrationWarning
						id={inputId}
						disabled={disabled}
						required={!optional}
						aria-invalid={invalid || undefined}
						aria-describedby={[hint ? hintId : null, counterId].filter(Boolean).join(' ') || undefined}
						maxLength={500}
						style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
						placeholder={placeholder || ''}
						onChange={onChange}
						value={value}
						rows={4}
						className={`
						pb-[1rem]
						w-full
						focus:outline-none focus:ring-0
						${SIZE_TO_TEXT_STYLE[size]}
						${STATE_TO_TEXT[getState(disabled, isFilled, invalid)]}
					`}
					/>
					{/* Character counter positioned over the textarea near the resize handle */}
					<span
						id={counterId}
						aria-live="polite"
						className={`
							absolute bottom-0
							${LANGUAGE_TO_COUNTER_POSITION[language]}
							pointer-events-none
							pb-[0.25rem]
							text-12--alexandria
							${invalid ? 'text-state-error-base' : 'text-text-quinary'}
						`}
					>
						{value?.length}/500
					</span>
				</div>
			</div>
			<div
				style={{
					direction: LANGUAGE_TO_DIRECTION[language],
					paddingInlineEnd: '2rem',
					height: '1rem'
				}}
				className={`
					flex 
					flex-row
					gap-[0.25rem] 
					items-center 
					justify-start
					transition-opacity duration-300 ease-in-out
					${(hint || invalid) ? 'opacity-100' : 'opacity-0'}
				`}
			>
				{hint && <IconWrapper 
					className={`${STATE_TO_ICON_TEXT[getState(disabled, isFilled, invalid)]} self-center`} 
					size={16}
				>
					<Info />
				</IconWrapper>}
				<p
					id={hintId}
					role={invalid ? 'alert' : undefined}
					className={`
						text-12-fixed--alexandria
						${STATE_TO_HINT_TEXT[getState(disabled, isFilled, invalid)]}
					`}
				>
					{hint || " "}
				</p>
			</div>
		</div>
	);
};

export default TextAreaField;
